import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { WaitlistForm } from "@/components/landing/WaitlistForm";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const lineVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.9, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 130, damping: 18 },
  },
};

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } },
};

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // Scroll-out transforms for the headline block
  const y = useTransform(scrollYProgress, [0, 0.6], [0, -160]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.6], [1, 0.82]);
  const blur = useTransform(scrollYProgress, [0, 0.5], ["blur(0px)", "blur(10px)"]);

  return (
    <section
      ref={sectionRef}
      data-testid="hero"
      className="relative flex min-h-screen items-center justify-center overflow-hidden pt-24"
    >
      {/* video background */}
      <div className="absolute inset-0 -z-20 overflow-hidden">
        <video
          src="/media/hero.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="h-full w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-[#050505]/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/50 via-transparent to-[#050505]" />
      </div>

      {/* glows */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-40 bottom-0 h-[560px] w-[560px] glow-violet blur-2xl" />
        <div className="absolute -right-32 top-0 h-[520px] w-[520px] glow-orange blur-2xl" />
      </div>

      <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-8">
        <motion.div
          style={{ y, opacity, scale, filter: blur }}
          className="text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs font-medium uppercase tracking-[0.25em] text-white/70 backdrop-blur"
          >
            Your AI Life Operating System
          </motion.div>

          <motion.h1
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="font-mega chromatic text-[15vw] leading-[0.9] text-white sm:text-[12vw] lg:text-[9.5rem]"
          >
            <motion.span variants={lineVariants} className="block">
              Become
            </motion.span>
            <motion.span variants={lineVariants} className="block">
              Who You&rsquo;re
            </motion.span>
            <motion.span variants={lineVariants} className="block text-gradient">
              Meant to Be
            </motion.span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mx-auto mt-9 max-w-2xl text-lg text-muted-foreground sm:text-xl"
          >
            Your AI Life Coach for every goal, every dream, every day. Build wealth,
            get healthy, launch a business, or become a better version of yourself —
            b&rarr;come builds your roadmap and helps you execute it, one step at a time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-11 flex flex-col items-center justify-center gap-4"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/60">
              Launching soon &mdash; be the first to know
            </p>
            <WaitlistForm testid="hero-waitlist" />
            <button
              onClick={() => scrollTo("how")}
              data-testid="hero-how-btn"
              className="inline-flex h-11 items-center justify-center rounded-full border border-white/12 bg-white/[0.03] px-6 text-sm font-medium text-white backdrop-blur transition-colors hover:border-white/30"
            >
              See how it works
            </button>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollTo("how")}
        aria-label="Scroll down"
        style={{ opacity }}
        className="absolute bottom-8 right-8 hidden h-12 w-12 items-center justify-center rounded-full border border-white/12 bg-white/[0.03] text-white/70 backdrop-blur md:flex"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      >
        <ArrowDown className="h-5 w-5" />
      </motion.button>
    </section>
  );
};

export default Hero;
